/**
 * Timezone primitives — pure, framework-agnostic helpers shared by quiet-hours
 * evaluation and scheduled-message anchoring:
 *   - read the calendar parts of an instant as observed in a given IANA zone
 *   - convert wall-clock parts (interpreted in a zone) back to a UTC instant,
 *     DST-correct
 *   - compute calendar-day keys / spans in a zone
 *   - resolve the contact's timezone from a case
 *
 * No quiet-hours-specific logic lives here.
 */

import type { Job } from '@app/types/api/job';
import { DEFAULT_TIMEZONE } from '@app/types/api/organization';

/** Calendar parts of an instant as read in a given timezone (not a UTC moment). */
export interface ZonedDateParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  dayOfWeek: number; // 0=Sun..6=Sat (matches Date#getDay)
}

const DAY_NAME_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

// `Intl.DateTimeFormat` construction is expensive and react-datepicker re-runs
// this for every time slot and day cell on each render. A formatter is immutable
// and can be cached by time zone (all other inputs are currently fixed)
const zonedPartsFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getZonedPartsFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = zonedPartsFormatterCache.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  zonedPartsFormatterCache.set(timeZone, formatter);
  return formatter;
};

/** Date parts (year/month/day/hour/minute/dayOfWeek) of `instant` as a
 *  calendar in `timeZone` would read them. */
export const getDateParts = (
  instant: Date,
  timeZone: string,
): ZonedDateParts => {
  const parts = getZonedPartsFormatter(timeZone).formatToParts(instant);

  const lookup: Record<string, string> = {};
  parts.forEach((p) => {
    if (p.type !== 'literal') lookup[p.type] = p.value;
  });
  // Intl can emit "24" for midnight in some locales — normalise to 0.
  const rawHour = Number(lookup.hour);
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour: rawHour === 24 ? 0 : rawHour,
    minute: Number(lookup.minute),
    dayOfWeek: DAY_NAME_TO_INDEX[lookup.weekday] ?? 0,
  };
};

/**
 * Convert calendar parts (interpreted in `timeZone`) to UTC ms.
 *
 * Algorithm — single-pass offset measurement, DST-correct:
 *   1. Treat the input parts as if they were UTC. That gives a *candidate*
 *      UTC ms (`naiveUtc`) that's wrong by exactly the zone's offset at the
 *      target moment.
 *   2. Project the candidate back into `timeZone`. The result tells us what
 *      a calendar in that zone would read for the candidate.
 *   3. The delta between the input parts and the projected parts is the
 *      zone's offset (positive west of UTC, negative east).
 *   4. Add the delta back to `naiveUtc` to land on the true UTC ms.
 *
 * This works across DST transitions because step 2 asks `Intl` for the actual
 * offset that applies at that moment — no hard-coded ±5h/±4h table.
 */
export const partsToUtcMs = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number => {
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute);
  // What does a calendar in `timeZone` read for the naive candidate?
  const projectedParts = getDateParts(new Date(naiveUtc), timeZone);
  const projectedPartsAsUtc = Date.UTC(
    projectedParts.year,
    projectedParts.month - 1,
    projectedParts.day,
    projectedParts.hour,
    projectedParts.minute,
  );
  // (naiveUtc - projectedPartsAsUtc) is the zone's offset at this instant.
  return naiveUtc + (naiveUtc - projectedPartsAsUtc);
};

/**
 * Contact IANA timezone for the case. Mirrors the server's
 * `resolveContactTimeZone`: the Verisk inspection-address zone wins when
 * present, then the end-user's own zone (`user.timeZone ?? profile.timeZone`).
 * Null means the caller should apply its own fallback (e.g. org default) — none
 * is applied here.
 */
export const getContactTimeZone = (job: Job | undefined): string | null => {
  const veriskTimeZone = job?.veriskData?.timeZoneData?.timeZoneId;
  if (veriskTimeZone) return veriskTimeZone;
  const customer = job?.customer;
  if (!customer) return null;
  return customer.timeZone ?? customer.profile?.timeZone ?? null;
};

/** Where a resolved timezone came from. `verisk`/`contact` are derived from the
 *  case's contact; `org`/`default` are fallbacks (no contact zone was known). */
export type TimeZoneSource = 'verisk' | 'contact' | 'org' | 'default';

export interface ResolvedTimeZone {
  /** Always a concrete IANA zone — safe to schedule or evaluate against. */
  timeZone: string;
  source: TimeZoneSource;
}

/**
 * Unified case-timezone resolver shared by quiet hours and scheduled messages.
 * Resolution order mirrors the server (`getCachedTimeZone`): Verisk inspection
 * zone → contact profile → caller's org fallback → DEFAULT_TIMEZONE. Always
 * returns a concrete zone; `source` lets callers distinguish a contact-derived
 * zone from a fallback (e.g. the QH banner renders generic copy instead of a
 * misleading contact-local time when the zone is a fallback).
 */
export const resolveCaseTimeZone = (
  job: Job | undefined,
  fallbackTimeZone: string | undefined,
): ResolvedTimeZone => {
  const veriskTz = job?.veriskData?.timeZoneData?.timeZoneId;
  if (veriskTz) return { timeZone: veriskTz, source: 'verisk' };

  const contactTz = getContactTimeZone(job);
  if (contactTz) return { timeZone: contactTz, source: 'contact' };

  if (fallbackTimeZone) return { timeZone: fallbackTimeZone, source: 'org' };

  return { timeZone: DEFAULT_TIMEZONE, source: 'default' };
};

/** True when the resolved zone is the contact's own (Verisk or profile) rather
 *  than a fallback — i.e. safe to present as the contact's local time. */
export const isContactZone = (resolved: ResolvedTimeZone): boolean =>
  resolved.source === 'verisk' || resolved.source === 'contact';

// SSM kill switch (FE-only) for the unified timezone resolution path. When off,
// quiet hours and scheduled messages use their legacy per-feature resolution.
export const isUnifiedTimezoneResolutionFeatureFlagOn = (): boolean => {
  const flagValue = process.env
    .FEATURE_FLAG_UNIFIED_TIMEZONE_RESOLUTION as unknown as boolean | string;
  return flagValue === true || flagValue === 'true';
};

/**
 * Human-readable name for an IANA zone in `locale` (e.g. "Eastern Daylight
 * Time"), falling back to the raw id for unknown zones. Works for any IANA zone
 * — unlike the Admin `formatTimezoneLabel`, which assumes SUPPORTED_TIMEZONES.
 */
export const formatTimeZoneName = (
  timeZone: string,
  locale?: string,
): string => {
  try {
    const name = new Intl.DateTimeFormat(locale || 'en', {
      timeZone,
      timeZoneName: 'long',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    return name || timeZone;
  } catch {
    return timeZone;
  }
};

/**
 * A browser-local Date whose calendar fields equal `instant`'s wall-clock in
 * `timeZone`. Fed to a browser-local widget (react-datepicker) so it displays
 * and bounds dates in the contact's zone rather than the agent's browser.
 */
export const toZonedDisplayDate = (instant: Date, timeZone: string): Date => {
  const parts = getDateParts(instant, timeZone);
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
  );
};

/**
 * Converts a wall-clock time to a UTC instant (ms). When `timeZone` is set, the
 * wall-clock's calendar fields are interpreted in that zone. Otherwise they're
 * read in the browser's local zone.
 */
export const wallClockToUtcMs = (
  wallClock: Date,
  timeZone?: string | null,
): number =>
  timeZone
    ? partsToUtcMs(
        wallClock.getFullYear(),
        wallClock.getMonth() + 1,
        wallClock.getDate(),
        wallClock.getHours(),
        wallClock.getMinutes(),
        timeZone,
      )
    : wallClock.getTime();
